import React from 'react';
import { motion } from 'framer-motion';
import { CheckSquare, Heart, BookHeart, Wind } from 'lucide-react';
const features = [{
  icon: <CheckSquare size={28} data-id="element-39" data-mp="159td415y7vww" />,
  title: 'Daily Habits',
  description: 'Set and track daily wellness tasks. From drinking water to taking a walk, check them off to earn treats for your companion.',
  color: 'bg-peach',
  lightColor: 'bg-peach/10',
  textColor: 'text-peach'
}, {
  icon: <Heart size={28} data-id="element-40" data-mp="1j8t3bd940iv6" />,
  title: 'Mood Tracking',
  description: "Log how you're feeling each day. Notice patterns over time and let your buddy comfort you on the hard days.",
  color: 'bg-coral',
  lightColor: 'bg-coral/10',
  textColor: 'text-coral'
}, {
  icon: <BookHeart size={28} data-id="element-41" data-mp="1e7lt77jhv7lc" />,
  title: 'Gratitude Journal',
  description: "Take a moment to write down what you're thankful for. A positive mindset keeps your companion's energy high.",
  color: 'bg-lavender',
  lightColor: 'bg-lavender/10',
  textColor: 'text-lavender'
}, {
  icon: <Wind size={28} data-id="element-42" data-mp="2xuq3nxzpty8h" />,
  title: 'Breathing Exercises',
  description: "Guided moments to pause and breathe. Watch your companion's chest rise and fall to match your breathing pace.",
  color: 'bg-sage',
  lightColor: 'bg-sage/10',
  textColor: 'text-sage'
}];
export function Features() {
  return <section id="features" className="py-24 bg-cream relative overflow-hidden" data-id="element-43" data-mp="32v4a5blo504e">
      {/* Decorative blobs */}
      <div className="absolute top-40 right-[-10%] w-[30rem] h-[30rem] bg-sage/10 blob-shape -z-10 blur-3xl" data-id="element-44" data-mp="3gqg3bek6eij7" />
      <div className="absolute bottom-10 left-[-5%] w-[25rem] h-[25rem] bg-peach/10 blob-shape -z-10 blur-3xl" style={{
      animationDelay: '-2s'
    }} data-id="element-45" data-mp="3qim4newbicb2" />

      <div className="max-w-7xl mx-auto px-6" data-id="element-46" data-mp="3llvge4ad0aek">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-47" data-mp="3exunhg5msfv">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-48" data-mp="3ukh5kiac6nob">
            <h2 className="text-4xl md:text-5xl font-black text-warmBrown mb-6 leading-tight" data-id="element-49" data-mp="316o89vo7taya">
              Everything you need to{' '}
              <span className="text-peach" data-id="element-50" data-mp="3600wi4ozffqi">feel your best</span>
            </h2>
            <p className="text-lg text-warmBrown/70 mb-8 leading-relaxed" data-id="element-51" data-mp="3ax12kflu0ihm">
              Pawchi isn't just a cute face. It's a comprehensive toolkit
              designed to help you build sustainable routines, manage stress,
              and find moments of joy in your everyday life.
            </p>

            <div className="hidden lg:block relative w-full max-w-md aspect-square bg-white rounded-[3rem] shadow-soft-lg p-8" data-id="element-52" data-mp="3ouxyd9sgokv8">
              <div className="absolute inset-0 bg-gradient-to-br from-coral/10 to-transparent rounded-[3rem]" data-id="element-53" data-mp="1uirbhdq7gfn3" />
              <div className="relative h-full flex flex-col justify-center items-center text-center" data-id="element-54" data-mp="1n01njb6k5ngt">
                <div className="w-32 h-32 bg-cream rounded-full flex items-center justify-center mb-6 shadow-inner overflow-hidden" data-id="element-55" data-mp="1i8jjufd5h8s5">
                  <img src="https://cdn.magicpatterns.com/uploads/n3sy5rTKRStSskc1tVn4iu/meditate.png" alt="Meditating dog" className="w-24 h-24 object-contain" data-id="element-56" data-mp="1da881z3aoo7a" />
                </div>
                <h3 className="text-2xl font-bold text-warmBrown mb-2" data-id="element-57" data-mp="20yrf6balyuxs">
                  Breathe with Barnaby
                </h3>
                <p className="text-warmBrown/60" data-id="element-58" data-mp="1ww4le4udmzxy">Inhale... Exhale...</p>

                {/* Animated breathing circle */}
                <motion.div animate={{
                scale: [1, 1.5, 1]
              }} transition={{
                repeat: Infinity,
                duration: 4,
                ease: 'easeInOut'
              }} className="w-16 h-16 rounded-full border-4 border-sage/30 mt-8" data-id="element-59" data-mp="1s0p93hc0pc3n" />
              </div>
            </div>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6" data-id="element-60" data-mp="1vfz6pxdh5jzc">
            {features.map((feature, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} whileHover={{
            scale: 1.03
          }} className="bg-white p-8 rounded-3xl shadow-soft hover:shadow-soft-lg transition-all" data-id="element-61" data-mp="1zg9enfktukgu" data-mpi={index}>
                <div className={`w-14 h-14 rounded-2xl ${feature.lightColor} ${feature.textColor} flex items-center justify-center mb-6`} data-id="element-62" data-mp="17ns62m0puqu4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-warmBrown mb-3" data-id="element-63" data-mp="196s59d3x4bft">
                  {feature.title}
                </h3>
                <p className="text-warmBrown/70 text-sm leading-relaxed" data-id="element-64" data-mp="148z2ku1tpzka">
                  {feature.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}